<!-- ETC管理 -->
<template>
  <v-container fluid>
    <!-- 查询条件：卡号、状态 -->
    <v-row class="query-row">
      <v-col cols="12" xl="2" lg="2" md="4" sm="6">
        <v-text-field v-model="queryForm.card_no" label="卡号" dense outlined clearable hide-details
          @keyup.enter="search()"></v-text-field>
      </v-col>
      <v-col cols="12" xl="2" lg="2" md="4" sm="6">
        <v-select v-model="queryForm.status" :items="statusOptions" item-text="label" item-value="value"
          label="状态" no-data-text="无" dense outlined clearable hide-details
          :menu-props="{ bottom: true, offsetY: true }">
        </v-select>
      </v-col>
      <v-col cols="12" xl="3" lg="3" md="6" sm="6">
        <a-space>
          <v-btn @click="search()" color="primary" dark tile>
            <v-icon small>mdi-magnify</v-icon>查询
          </v-btn>
          <v-btn @click="openCreate" color="success" dark tile>
            <v-icon small>mdi-plus</v-icon>新增ETC
          </v-btn>
        </a-space>
      </v-col>
    </v-row>

    <v-card tile>
      <v-card-title class="d-flex align-center py-2 px-3" style="background-color: #fff;">
        <span class="text-subtitle-2">共 {{ count }} 条数据</span>
        <v-spacer></v-spacer>
        <v-menu bottom offset-y>
          <template v-slot:activator="{ on, attrs }">
            <v-btn small outlined v-bind="attrs" v-on="on" class="grey lighten-3">{{ limit }}/页</v-btn>
          </template>
          <v-list>
            <v-list-item v-for="item in pages" :key="item.value" @click="changeLimit(item.value)">
              <v-list-item-title>{{ item.value }}/页</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
      </v-card-title>
      <a-table size="small" :columns="headers" :data-source="pageList" row-key="_id" :pagination="false" bordered
        :locale="{ emptyText: '暂无数据' }" :scroll="{ x: width }">
        <!-- 序号：按分页连续编号 -->
        <template slot="index" slot-scope="text, record, index">
          {{ (page - 1) * limit + index + 1 }}
        </template>
        <!-- 充值金额、余额保留两位小数 -->
        <template slot="recharge_amount" slot-scope="text">
          <span class="font-weight-bold">{{ formatMoney(text) }}</span>
        </template>
        <template slot="balance" slot-scope="text">
          <span class="font-weight-bold" :class="Number(text) < 0 ? 'error--text' : ''">{{ formatMoney(text) }}</span>
        </template>
        <!-- 备注超长省略 -->
        <template slot="remark" slot-scope="text">
          <a-tooltip v-if="text" :title="text" placement="topLeft">
            <span>{{ text }}</span>
          </a-tooltip>
        </template>
        <!-- 状态：1 启用 / 0 禁用 -->
        <template slot="status" slot-scope="text">
          <a-tag :color="text === 1 ? 'green' : 'red'">{{ text === 1 ? "启用" : "禁用" }}</a-tag>
        </template>
        <!-- 操作：编辑 / 删除（nowrap 防止挤压换行；列少不固定列） -->
        <template slot="action" slot-scope="text, record">
          <a-space :size="4" style="white-space: nowrap;">
            <a @click="openEdit(record)">编辑</a>
            <a-divider type="vertical" />
            <a-popconfirm :title="`确认删除ETC卡「${record.card_no}」?`" ok-text="确定" cancel-text="取消" placement="left"
              @confirm="removeItem(record)">
              <a class="error--text">删除</a>
            </a-popconfirm>
          </a-space>
        </template>
      </a-table>
    </v-card>

    <v-card tile class="mt-2">
      <v-pagination v-if="length > 1" v-model="page" :page="page" :length="length" total-visible="10"></v-pagination>
    </v-card>

    <!-- 新增 / 编辑ETC弹窗 -->
    <v-dialog v-model="dialogVisible" max-width="720" persistent scrollable>
      <v-card>
        <v-card-title class="primary white--text">
          {{ dialogMode === "create" ? "新增ETC" : "编辑ETC" }}
        </v-card-title>
        <v-card-text class="pt-4">
          <v-form ref="form" v-model="formValid" :lazy-validation="true">
            <v-row>
              <v-col cols="12" md="6" sm="6">
                <!-- 卡号可能为长数字串，使用文本避免精度丢失 -->
                <v-text-field v-model="form.card_no" label="卡号" dense outlined required
                  :rules="[v => !!v || '请填写卡号']"></v-text-field>
              </v-col>
              <v-col cols="12" md="6" sm="6">
                <v-menu ref="open_date_menu" v-model="open_date_menu" :close-on-content-click="false"
                  :return-value.sync="form.open_date" transition="scale-transition" min-width="auto" offset-y>
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field v-model="form.open_date" label="开卡日期" append-icon="mdi-calendar" readonly
                      v-bind="attrs" v-on="on" dense outlined clearable required hide-details
                      :rules="[v => !!v || '请选择开卡日期']"></v-text-field>
                  </template>
                  <v-date-picker v-model="form.open_date" no-title scrollable locale="zh-cn"
                    @input="() => { $refs.open_date_menu.save(form.open_date); open_date_menu = false; }"></v-date-picker>
                </v-menu>
              </v-col>
              <v-col cols="12" md="6" sm="6">
                <v-text-field v-model.number="form.recharge_amount" label="充值金额(元)" type="number" dense outlined
                  :rules="[v => v === '' || v == null || Number(v) >= 0 || '充值金额不能为负']"></v-text-field>
              </v-col>
              <v-col cols="12" md="6" sm="6">
                <v-text-field v-model.number="form.balance" label="余额(元)" type="number" dense outlined></v-text-field>
              </v-col>
              <v-col cols="12" md="6" sm="6">
                <!-- 状态：1 启用 / 0 禁用 -->
                <v-radio-group v-model="form.status" row class="mt-0 pt-2">
                  <v-radio color="primary" label="启用" :value="1"></v-radio>
                  <v-radio color="error" label="禁用" :value="0"></v-radio>
                </v-radio-group>
              </v-col>
              <v-col cols="12">
                <v-textarea v-model="form.remark" label="备注" dense outlined rows="2"></v-textarea>
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text tile @click="dialogVisible = false">取消</v-btn>
          <v-btn color="primary" dark tile @click="submitForm">保存</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import _ from "underscore";

export default {
  name: "Etccard",
  data() {
    return {
      // mock 开关：true=本地模拟数据；后端接口出来后改为 false，并打开 getdata/save/remove 里的真实请求
      useMock: true,
      // 查询条件：卡号、状态
      queryForm: {
        card_no: "",
        status: null,
      },
      // 状态选项：1 启用 / 0 禁用
      statusOptions: [
        { label: "启用", value: 1 },
        { label: "禁用", value: 0 },
      ],
      pages: [
        { text: "20/页", value: 20 },
        { text: "50/页", value: 50 },
        { text: "100/页", value: 100 },
      ],
      headers: [
        { title: "序号", dataIndex: "index", scopedSlots: { customRender: "index" }, width: 70, align: "center" },
        { title: "卡号", dataIndex: "card_no", width: 220, align: "center" },
        { title: "充值金额(元)", dataIndex: "recharge_amount", scopedSlots: { customRender: "recharge_amount" }, width: 130, align: "center" },
        { title: "余额(元)", dataIndex: "balance", scopedSlots: { customRender: "balance" }, width: 120, align: "center" },
        { title: "状态", dataIndex: "status", scopedSlots: { customRender: "status" }, width: 90, align: "center" },
        { title: "备注", dataIndex: "remark", scopedSlots: { customRender: "remark" }, width: 200, align: "left", ellipsis: true },
        { title: "开卡日期", dataIndex: "open_date", width: 120, align: "center" },
        { title: "操作", dataIndex: "action", scopedSlots: { customRender: "action" }, width: 130, align: "center" },
      ],
      // 全量 mock 数据
      allList: [],
      // 查询过滤后的数据
      list: [],
      page: 1,
      limit: 20,
      count: 0,
      length: 0,
      // 新增/编辑弹窗
      dialogVisible: false,
      dialogMode: "create", // create / edit
      formValid: false,
      form: this.emptyForm(),
      open_date_menu: false,
    };
  },
  computed: {
    width() {
      return _.reduce(this.headers, (a, b) => a + (b.width || 120), 0);
    },
    // 当前页数据（前端分页）
    pageList() {
      const start = (this.page - 1) * this.limit;
      return this.list.slice(start, start + this.limit);
    },
  },
  mounted() {
    this.getdata();
  },
  methods: {
    emptyForm() {
      return {
        _id: "",
        card_no: "",
        recharge_amount: 0,
        balance: 0,
        status: 1, // 默认启用
        remark: "",
        open_date: "",
      };
    },
    formatMoney(v) {
      return v == null || v === "" ? "0.00" : Number(v).toFixed(2);
    },
    search() {
      this.page = 1;
      this.getdata();
    },
    getdata() {
      if (this.useMock) {
        this.getMockData();
        return;
      }
      // TODO 接口联调：POST /etccard/list
      // 参数：card_no 卡号(模糊)；status 状态 1启用/0禁用
      // this.$toast.loading({ message: "" });
      // this.$api.doAPI("/etccard/list", { page: this.page, limit: this.limit, ...this.queryForm }).then((res) => {
      //   this.$toast.clear();
      //   if (res.code === 0) {
      //     this.list = res.data.list;
      //     this.count = res.data.count;
      //     this.length = res.data.length || Math.max(1, Math.ceil(this.count / this.limit));
      //   } else {
      //     this.$alert(res.msg);
      //   }
      // }).catch((err) => { this.$toast.clear(); console.error(err); });
    },
    // 模拟接口数据
    getMockData() {
      if (!this.allList.length) {
        const raw = [
          { card_no: "4101234567890123", recharge_amount: 10000, balance: 2380.5, status: 1, remark: "豫A37230D绑定ETC", open_date: "2025-02-16" },
          { card_no: "4101234567890456", recharge_amount: 8000, balance: 1520, status: 1, remark: "豫A37301D绑定ETC", open_date: "2025-04-09" },
          { card_no: "4101234567890789", recharge_amount: 5000, balance: -126.8, status: 1, remark: "余额不足，请及时充值", open_date: "2025-06-21" },
          { card_no: "4101234567891011", recharge_amount: 3000, balance: 0, status: 0, remark: "车辆已转出，卡片停用", open_date: "2024-09-03" },
          { card_no: "3201987654321098", recharge_amount: 6000, balance: 980.35, status: 1, remark: "", open_date: "2025-08-14" },
        ];
        this.allList = raw.map((it, i) => ({
          _id: "etc_" + (i + 1),
          ...it,
        }));
      }
      // 条件过滤：卡号 + 状态
      let result = [...this.allList];
      if (this.queryForm.card_no) {
        const kw = this.queryForm.card_no.trim();
        result = result.filter((it) => (it.card_no || "").includes(kw));
      }
      if (this.queryForm.status !== null && this.queryForm.status !== undefined && this.queryForm.status !== "") {
        result = result.filter((it) => it.status === this.queryForm.status);
      }
      this.list = result;
      this.count = result.length;
      this.length = Math.max(1, Math.ceil(this.count / this.limit));
      if (this.page > this.length) this.page = 1;
    },
    changeLimit(v) {
      this.limit = v;
      this.page = 1;
      this.getdata();
    },
    openCreate() {
      this.form = this.emptyForm();
      this.dialogMode = "create";
      this.dialogVisible = true;
      this.$nextTick(() => this.$refs.form && this.$refs.form.resetValidation());
    },
    openEdit(record) {
      this.form = {
        _id: record._id,
        card_no: record.card_no || "",
        recharge_amount: record.recharge_amount,
        balance: record.balance,
        status: record.status,
        remark: record.remark || "",
        open_date: record.open_date || "",
      };
      this.dialogMode = "edit";
      this.dialogVisible = true;
      this.$nextTick(() => this.$refs.form && this.$refs.form.resetValidation());
    },
    submitForm() {
      this.$refs.form.validate((valid) => {
        if (!valid) return;
        if (this.useMock) {
          this.saveMock();
          return;
        }
        // TODO 接口联调：POST /etccard/save（新增/编辑，编辑传 _id）
        // this.$toast.loading({ message: "" });
        // this.$api.doAPI("/etccard/save", { ...this.form }).then((res) => {
        //   this.$toast.clear();
        //   if (res.code === 0) {
        //     this.$message.success(this.dialogMode === "create" ? "新增成功" : "已更新");
        //     this.dialogVisible = false;
        //     this.getdata();
        //   } else {
        //     this.$alert(res.msg);
        //   }
        // }).catch((err) => { this.$toast.clear(); console.error(err); });
      });
    },
    saveMock() {
      const data = {
        ...this.form,
        recharge_amount: Number(this.form.recharge_amount) || 0,
        balance: Number(this.form.balance) || 0,
      };
      if (this.dialogMode === "create") {
        this.allList = [{ ...data, _id: "etc_" + Date.now() }, ...this.allList];
        this.$message.success("新增成功");
      } else {
        this.allList = this.allList.map((it) => (it._id === data._id ? { ...it, ...data } : it));
        this.$message.success("已更新");
      }
      this.dialogVisible = false;
      this.getdata();
    },
    removeItem(record) {
      if (this.useMock) {
        this.allList = this.allList.filter((it) => it._id !== record._id);
        this.$message.success("已删除");
        this.getdata();
        return;
      }
      // TODO 接口联调：POST /etccard/remove
      // 参数：_id ETC卡ID
      // this.$toast.loading({ message: "" });
      // this.$api.doAPI("/etccard/remove", { _id: record._id }).then((res) => {
      //   this.$toast.clear();
      //   if (res.code === 0) {
      //     this.$message.success("已删除");
      //     this.getdata();
      //   } else {
      //     this.$alert(res.msg);
      //   }
      // }).catch((err) => { this.$toast.clear(); console.error(err); });
    },
  },
};
</script>
<style lang="less" scoped>
/deep/ .row {
  margin-bottom: 0 !important;
}

/* 查询条件行与下方表格保持间距 */
/deep/ .row.query-row {
  margin-bottom: 16px !important;
}

/deep/ .col,
.col-12 {
  padding-bottom: 0 !important;
}

/deep/ .v-input--selection-controls {
  margin-top: 0;
  padding-top: 0;
}

p {
  margin: 0;
}
</style>
